// The demo dataset. Built fresh (relative to *today*) the first time a browser
// opens the demo, and again whenever the visitor hits "Reset demo data", so the
// hareline always straddles the current date no matter when the demo is opened.
//
// Seed ids are stable, hand-written strings (k_nyc, h_admin, …) so the selected
// login identity and any deep links survive a reseed. Everything here is plain
// domain data from @hash/shared — no AWS, no PK/SK; the demo store is the table.

import {
  kennelDisplayName,
  membershipState,
  scheduleOccurrences,
  trailStartAt,
  type Attendance,
  type Hasher,
  type Kennel,
  type Membership,
  type MembershipScheme,
  type Payment,
  type RoleAssignment,
  type RoleAuditRecord,
  type Trail,
  type TrailHistoryEntry,
} from "@hash/shared";

export interface DemoDb {
  kennels: Kennel[];
  hashers: Hasher[];
  schemes: MembershipScheme[];
  memberships: Membership[];
  trails: Trail[];
  attendance: Attendance[];
  payments: Payment[];
  roleAssignments: RoleAssignment[];
  roleAudit: RoleAuditRecord[];
  trailHistory: TrailHistoryEntry[];
}

// --- date helpers (all UTC, date granularity) --------------------------------

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function addDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

// Small deterministic PRNG so a reseed on the same day looks identical run to
// run (nice for screenshots) without pulling in a dependency.
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- people ------------------------------------------------------------------

interface HasherSpec {
  id: string;
  name: string;
  kennel: Kennel;
  role?: "admin" | "hare";
  state?: Hasher["accountState"];
  phone?: string;
  visitor?: boolean;
  // Optional real name + email (required before dues can be recorded). Seeded
  // on the dues-paying hashers; left off the rest to showcase the gate.
  nerdName?: string;
  email?: string;
}

function makeHasher(spec: HasherSpec, createdAt: string): Hasher {
  return {
    id: spec.id,
    name: spec.name,
    nerdName: spec.nerdName,
    email: spec.email,
    homeKennelId: spec.kennel.id,
    // Hasher.homeKennel caches the *full* kennel name (Trail.kennel caches the
    // display name) — keep that distinction so the demo matches real fan-out.
    homeKennel: spec.kennel.name,
    phone: spec.phone,
    role: spec.role ?? "hare",
    visitor: spec.visitor ?? !spec.kennel.managed,
    accountState: spec.state ?? "claimed",
    createdAt,
  };
}

export function buildSeed(): DemoDb {
  const today = new Date(`${ymd(new Date())}T00:00:00Z`);
  const todayStr = ymd(today);
  const rand = mulberry32(0x9e3779b9 ^ today.getUTCFullYear());
  const pick = <T>(xs: T[]): T => xs[Math.floor(rand() * xs.length)];
  const createdLongAgo = addDays(today, -400).toISOString();

  // --- kennels ---------------------------------------------------------------
  const nyc: Kennel = {
    id: "k_nyc",
    name: "Hash House Harriers New York",
    shortName: "Hash NYC",
    managed: true,
    city: "New York",
    region: "NY",
    founded: "1989-05-01",
    doorFee: 1000,
    schedule: {
      recurrence: { freq: "weekly", weekday: 1 }, // Mondays
      time: "19:00",
      timeZone: "America/New_York",
      horizonMonths: 6,
    },
    createdAt: createdLongAgo,
  };
  const bk: Kennel = {
    id: "k_bk",
    name: "Brooklyn Hash House Harriers",
    shortName: "BH3",
    managed: true,
    city: "Brooklyn",
    region: "NY",
    founded: "2002-09-14",
    doorFee: 700,
    schedule: {
      recurrence: { freq: "biweekly", weekday: 3 }, // every other Wednesday
      time: "19:00",
      timeZone: "America/New_York",
      horizonMonths: 6,
    },
    createdAt: createdLongAgo,
  };
  const boston: Kennel = {
    id: "k_bos",
    name: "Boston Hash House Harriers",
    shortName: "Boston H3",
    managed: false,
    city: "Boston",
    region: "MA",
    createdAt: createdLongAgo,
  };
  const kennels = [nyc, bk, boston];

  // --- hashers ---------------------------------------------------------------
  const specs: HasherSpec[] = [
    { id: "h_admin", name: "Party Cock", kennel: nyc, role: "admin", phone: "+15551234567", nerdName: "Mike Stevens", email: "mike@example.com" },
    { id: "h_karen", name: "Sloppy Seconds", kennel: nyc, phone: "+15551112222", nerdName: "Karen Doyle", email: "karen@example.com" },
    { id: "h_nyc_lost", name: "Lost & Pound", kennel: nyc, phone: "+15551113333", nerdName: "Paul Rivera", email: "paul@example.com" },
    { id: "h_nyc_wrong", name: "Wrong Way", kennel: nyc, nerdName: "Tom Walsh", email: "tom@example.com" },
    { id: "h_nyc_chuck", name: "Two Buck Chuck", kennel: nyc, state: "lite" },
    { id: "h_nyc_dave", name: "Just Dave", kennel: nyc, nerdName: "Dave Kim", email: "dave@example.com" },
    { id: "h_nyc_mix", name: "Trail Mix", kennel: nyc },
    { id: "h_nyc_buns", name: "Buns of Steel", kennel: nyc, state: "lite" },
    { id: "h_nyc_whip", name: "Whip It Good", kennel: nyc, nerdName: "Wendy Cho", email: "wendy@example.com" },
    { id: "h_nyc_loin", name: "Sir Loin", kennel: nyc, state: "unclaimed", phone: "+15551114444" },

    { id: "h_sticky", name: "Famous Fingers", kennel: bk, phone: "+15552223333" },
    { id: "h_bk_hop", name: "Hop Scotch", kennel: bk },
    { id: "h_bk_goggles", name: "Beer Goggles", kennel: bk },
    { id: "h_bk_pat", name: "Just Pat", kennel: bk, state: "lite" },
    { id: "h_bk_dancer", name: "Tiny Dancer", kennel: bk },
    { id: "h_bk_pint", name: "Half-Pint", kennel: bk, state: "lite" },

    { id: "h_bos_bean", name: "Bean Town", kennel: boston, state: "lite" },
    { id: "h_bos_chow", name: "Chowder Head", kennel: boston, state: "lite" },
  ];
  const hashers = specs.map((s) => makeHasher(s, createdLongAgo));
  const byId = new Map(hashers.map((h) => [h.id, h]));
  const inKennel = (kid: string) => hashers.filter((h) => h.homeKennelId === kid);

  // --- membership scheme + memberships --------------------------------------
  const year = today.getUTCFullYear();
  const scheme: MembershipScheme = {
    id: "s_nyc_annual",
    name: "NYC Annual Dues",
    kennelIds: [nyc.id],
    price: 5000,
    recurrence: { months: 12, anchor: `${year}-01-01` },
    windowDays: 45,
    createdAt: createdLongAgo,
  };
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  // Roughly half of NYC's claimed hashers have paid this year's dues.
  const duesPayers = ["h_admin", "h_karen", "h_nyc_lost", "h_nyc_dave", "h_nyc_whip"];
  const memberships: Membership[] = duesPayers.map((hid) => ({
    id: `${hid}#${nyc.id}`,
    hasherId: hid,
    kennelId: nyc.id,
    periods: [
      {
        start: yearStart,
        end: yearEnd,
        periodId: `${scheme.id}#${yearStart}`,
        schemeId: scheme.id,
      },
    ],
    createdAt: createdLongAgo,
    updatedAt: createdLongAgo,
  }));
  const membershipOf = (hasherId: string, kennelId: string) =>
    memberships.find((m) => m.hasherId === hasherId && m.kennelId === kennelId);

  // --- trails, attendance, payments -----------------------------------------
  const trails: Trail[] = [];
  const attendance: Attendance[] = [];
  const payments: Payment[] = [];
  const lastAttended = new Map<string, string>();

  const genFor = (kennel: Kennel, base: number) => {
    const sched = kennel.schedule!;
    const from = ymd(addDays(today, -70));
    const to = ymd(addDays(today, 80));
    const dates = scheduleOccurrences(sched.recurrence, from, to).sort();
    const roster = inKennel(kennel.id);
    let number = base;
    dates.forEach((date, idx) => {
      number += 1;
      const startAt = trailStartAt(date, sched.time, sched.timeZone) ?? undefined;
      const isPast = date < todayStr;
      // Hares: every past trail had one or two; only the next couple of future
      // trails are spoken for, the rest sit TBD for the visitor to claim.
      const futureIdx = dates.filter((d) => d >= todayStr).indexOf(date);
      const hasHares = isPast || (futureIdx >= 0 && futureIdx < 2);
      const hareCount = hasHares ? (rand() < 0.4 ? 2 : 1) : 0;
      const hares: string[] = [];
      while (hares.length < hareCount && roster.length) {
        const h = pick(roster);
        if (!hares.includes(h.id)) hares.push(h.id);
      }
      const trail: Trail = {
        id: `${kennel.id}#${number}`,
        eventType: "trail",
        kennelId: kennel.id,
        kennel: kennelDisplayName(kennel),
        number,
        date,
        startAt,
        hareHasherIds: hares,
        everHadHare: hares.length > 0,
        startLocation: isPast || hasHares ? pick(START_LOCATIONS) : "",
        title: hasHares && rand() < 0.5 ? pick(TRAIL_TITLES) : undefined,
        version: 0,
        createdAt: addDays(today, -75).toISOString(),
        createdByHasherId: hares[0] ?? "h_admin",
      };
      trails.push(trail);

      // Attendance only for the recent past, to keep the dataset light while
      // still giving the history/recent-attendee views something to show.
      const recentPast = isPast && idx >= dates.length - 8;
      if (!recentPast) {
        // Hares are auto-checked-in even on older trails (mirrors the server).
        for (const hid of hares) recordAttendance(trail, byId.get(hid)!, false);
        return;
      }
      const crowd = new Set<string>(hares);
      const target = 6 + Math.floor(rand() * 5);
      while (crowd.size < target && crowd.size < roster.length) {
        crowd.add(pick(roster).id);
      }
      // A couple of visiting hashers from out of town drop in.
      if (rand() < 0.5) crowd.add("h_bos_bean");
      for (const hid of crowd) {
        const h = byId.get(hid);
        if (h) recordAttendance(trail, h, rand() < 0.3);
      }
    });
  };

  function recordAttendance(trail: Trail, h: Hasher, virgin: boolean) {
    const state = membershipState(membershipOf(h.id, trail.kennelId), trail.date);
    const owes = state !== "active";
    attendance.push({
      trailId: trail.id,
      hasherId: h.id,
      nameSnapshot: h.name,
      paid: owes, // door collected on the night
      virgin,
      visitor: h.visitor || h.homeKennelId !== trail.kennelId,
      checkedInAt: `${trail.date}T23:30:00Z`,
      checkedInByHasherId: trail.hareHasherIds[0] ?? "h_admin",
    });
    if (owes) {
      const kennel = kennels.find((k) => k.id === trail.kennelId);
      payments.push({
        id: genPaymentId(trail.id, h.id),
        hasherId: h.id,
        kennelId: trail.kennelId,
        trailId: trail.id,
        kind: "door",
        amount: kennel?.doorFee ?? 0,
        method: "cash",
        state: "paid",
        recordedByHasherId: trail.hareHasherIds[0] ?? "h_admin",
        createdAt: `${trail.date}T23:35:00Z`,
      });
    }
    const prev = lastAttended.get(h.id);
    if (!prev || trail.date > prev) lastAttended.set(h.id, trail.date);
  }

  genFor(nyc, 1900);
  genFor(bk, 870);

  // A special event (Milestone 9) ~3 weeks out — charges everyone a fee.
  const rddDate = ymd(addDays(today, 21));
  trails.push({
    id: "k_nyc#reddress",
    eventType: "special",
    kennelId: nyc.id,
    kennel: kennelDisplayName(nyc),
    name: "Red Dress Run",
    date: rddDate,
    startAt: trailStartAt(rddDate, "14:00", nyc.schedule!.timeZone) ?? undefined,
    hareHasherIds: ["h_karen"],
    hareNames: undefined,
    everHadHare: true,
    startLocation: "Washington Square Park arch",
    title: "Annual Red Dress Run",
    description: "Wear red. All proceeds to charity. On-after at the usual bar.",
    specialFee: true,
    memberPrice: 2000,
    nonMemberPrice: 3000,
    version: 1,
    createdAt: addDays(today, -10).toISOString(),
    createdByHasherId: "h_karen",
  });

  // Reflect each hasher's most recent attended date for the "recent" view.
  for (const h of hashers) {
    const d = lastAttended.get(h.id);
    if (d) h.lastAttendedAt = d;
  }

  // --- roles -----------------------------------------------------------------
  const grantedAt = addDays(today, -200).toISOString();
  const roleAssignments: RoleAssignment[] = [
    {
      hasherId: "h_karen",
      kennelId: nyc.id,
      hasherName: "Sloppy Seconds",
      kennelName: nyc.name,
      grantedByHasherId: "h_admin",
      grantedAt,
    },
    {
      hasherId: "h_sticky",
      kennelId: bk.id,
      hasherName: "Famous Fingers",
      kennelName: bk.name,
      grantedByHasherId: "h_admin",
      grantedAt,
    },
  ];
  const roleAudit: RoleAuditRecord[] = roleAssignments.map((r, i) => ({
    id: `ra_${i}`,
    action: "grant",
    role: "misman",
    targetHasherId: r.hasherId,
    targetName: r.hasherName,
    kennelId: r.kennelId,
    kennelName: r.kennelName,
    actorHasherId: "h_admin",
    actorName: "Party Cock",
    at: grantedAt,
  }));

  return {
    kennels,
    hashers,
    schemes: [scheme],
    memberships,
    trails,
    attendance,
    payments,
    roleAssignments,
    roleAudit,
    trailHistory: [],
  };
}

let paymentCounter = 0;
function genPaymentId(trailId: string, hasherId: string): string {
  paymentCounter += 1;
  return `pay_${trailId}_${hasherId}_${paymentCounter}`;
}

const START_LOCATIONS = [
  "The Dead Rabbit, 30 Water St",
  "McSorley's Old Ale House",
  "Prospect Park, Grand Army Plaza",
  "Astoria Park, under the bridge",
  "Tompkins Square Park, NE corner",
];
const TRAIL_TITLES = [
  "Hangover Recovery Trail",
  "Full Moon Shenanigans",
  "Bridges & Tunnels",
  "The Long Way Home",
  "Shiggy Special",
];
